import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, MapPin, Star, ArrowRight, Waves, Utensils, Home as HomeIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { EspacioCard, TestimonioCard } from "@/components/Cards";
import { espacios, testimonios } from "@/data/index";
import { ROUTE_PATHS } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
const springPresets = {
  gentle: {
    stiffness: 300,
    damping: 35
  },
  smooth: {
    stiffness: 200,
    damping: 40
  }
};
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export default function Home() {
  return <Layout>
      {/* Hero Section */}
      <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.HERO_BEACH_1} alt="Playa El Maviri" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-background" />
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={springPresets.smooth} className="container relative z-10 px-4 text-center text-white">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
            <MapPin className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium tracking-wider uppercase">
              Playa El Maviri • Ahome, Sinaloa
            </span>
          </motion.div>

          <h1 className="text-5xl md:text-8xl font-bold mb-6 tracking-tighter">
            Las <span className="text-accent">Gaviotas</span>
          </h1>

          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
            Descubre el santuario donde el desierto se encuentra con el mar. 
            Sabor tradicional, lujo costero y atardeceres infinitos.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild className="rounded-full px-8 h-14 text-lg bg-primary hover:bg-primary/90">
              <Link to={ROUTE_PATHS.ESPACIOS}>
                Explorar el Complejo
                <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="rounded-full px-8 h-14 text-lg border-white text-white hover:bg-white/10 backdrop-blur-sm">
              <a href="#espacios">Nuestros Espacios</a>
            </Button>
          </div>
        </motion.div>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-1">
            <div className="w-1 h-2 bg-white rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Introducción / Valor */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-primary">
                Una experiencia sensorial en el corazón de Sinaloa
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                En el complejo Las Gaviotas, hemos creado tres mundos distintos unidos por la misma pasión: la excelencia. Desde el sabor tradicional de nuestro restaurante insignia hasta la sofisticación de Medusas y la paz de nuestras cabañas.
              </p>
              <div className="grid sm:grid-cols-2 gap-6 pt-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center text-primary">
                    <Utensils className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold">Alta Cocina</h4>
                    <p className="text-sm text-muted-foreground">Mariscos frescos del día con recetas de autor.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center text-primary">
                    <HomeIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold">Refugio Privado</h4>
                    <p className="text-sm text-muted-foreground">Cabañas diseñadas para el descanso absoluto.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative aspect-square rounded-[2.5rem] overflow-hidden shadow-2xl">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2031455848650559488_ChatGPT Image 14 ene 2026, 03_18_02 p.m..png?image_process=quality,q_90/resize,w_1280/format,webp" alt="Atardecer en Las Gaviotas" className="w-full h-[592px] object-cover" />
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl">
                <p className="text-white italic text-lg">
                  "El Maviri no es solo un destino, es un estado mental donde el tiempo se detiene."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Espacios Section */}
      <section id="espacios" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} className="text-accent font-bold tracking-widest uppercase text-sm">
              Explora nuestra oferta
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">Tres destinos, una sola alma</h2>
            <p className="text-muted-foreground text-lg">
              Cada espacio en nuestro complejo ha sido diseñado para conectar con los elementos de Playa El Maviri de una forma única.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-3 gap-8">
            {espacios.map(espacio => <EspacioCard key={espacio.id} espacio={espacio} />)}
          </motion.div>
        </div>
      </section>

      {/* Testimonios Section */}
      <section id="testimonios" className="py-24 bg-background overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold mb-4">Lo que dicen nuestros visitantes</h2>
              <p className="text-muted-foreground text-lg">
                La satisfacción de nuestros clientes es el motor que nos impulsa a seguir ofreciendo lo mejor de la costa sinaloense.
              </p>
            </div>
            <div className="flex items-center gap-2 text-primary font-bold">
              <Star className="fill-accent text-accent" />
              <span className="text-2xl">4.9</span>
              <span className="text-muted-foreground">/ 5.0 Rating</span>
            </div>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid md:grid-cols-3 gap-8">
            {testimonios.map(testimonio => <TestimonioCard key={testimonio.id} testimonio={testimonio} />)}
          </motion.div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contacto" className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative z-10 bg-primary rounded-[3rem] p-12 md:p-20 text-white text-center overflow-hidden shadow-2xl shadow-primary/20">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <Waves className="absolute -top-10 -left-10 w-64 h-64 rotate-12" />
              <Waves className="absolute -bottom-10 -right-10 w-64 h-64 -rotate-12" />
            </div>

            <h2 className="text-4xl md:text-6xl font-bold mb-8 relative z-10">
              ¿Listo para vivir la experiencia <br />
              <span className="text-accent">Gaviotas?</span>
            </h2>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-12 relative z-10">
              Estamos ubicados en la zona más exclusiva de Playa El Maviri. Reserva tu mesa o cabaña y déjanos consentirte como te mereces.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 relative z-10">
              <Button size="lg" asChild className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10 h-14 text-lg font-bold">
                <Link to={ROUTE_PATHS.CONTACTO}>
                  Reservar Ahora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 h-14 text-lg">
                
              </Button>
            </div>

            <div className="mt-16 pt-16 border-t border-white/10 grid md:grid-cols-3 gap-8">
              <div>
                <h4 className="font-bold mb-2">Ubicación</h4>
                <p className="text-primary-foreground/60">Carretera Maviri-Topolobampo, <br />Isla El Maviri, Sinaloa.</p>
              </div>
              <div>
                <h4 className="font-bold mb-2">Contacto</h4>
                <p className="text-primary-foreground/60">+52 (668) 168 90 60         info@turismolasgaviotas.mx</p>
              </div>
              <div>
                <h4 className="font-bold mb-2">Horarios</h4>
                <p className="text-primary-foreground/60">Restaurante: 10am - 8pm<br />Cabañas: 24/7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>;
}